import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useReducer, useState } from 'react';
import type { ErrorWizardStepId, WizardContextValue, WizardStep, WizardStepId } from './wizardTypes';
import { InitializationStepId, WizardActionType } from './wizardTypes';
import { reducer } from './wizardReducer';
import { STEPS } from './wizardSteps';

export const WizardContext = createContext<WizardContextValue>({
  dispatch: () => {},
  currentIndex: 0,
  currentStep: {
    id: InitializationStepId,
    Component: () => null,
  },
  goToStep: () => {},
  goToErrorStep: () => {},
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  onNext: null,
  setOnNext: () => {},
  isNextDisabled: false,
  setIsNextDisabled: () => {},
  isBackDisabled: false,
  setIsBackDisabled: () => {},
  activeSteps: [],
  setActiveSteps: () => {},
});

export function WizardProvider({ children }: PropsWithChildren) {
  const [activeSteps, setActiveSteps] = useState<Array<WizardStep>>([]);

  const [state, dispatch] = useReducer(reducer, {
    currentStep: {
      id: InitializationStepId,
      Component: () => null,
    },
    activeSteps: STEPS,
  });
  const [onNext, setOnNext] = useState<(() => void | Promise<void>) | null>(null);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isBackDisabled, setIsBackDisabled] = useState(false);

  const currentIndex = activeSteps.findIndex((step) => step.id === state.currentStep.id);
  const currentStep = state.currentStep;

  const goToStep = useCallback((stepId: WizardStepId) => {
    dispatch({ type: WizardActionType.GOTO_STEP, stepId });
  }, []);

  const goToErrorStep = useCallback((stepId: ErrorWizardStepId) => {
    dispatch({ type: WizardActionType.GOTO_ERROR_STEP, stepId });
  }, []);

  const goToNextStep = useCallback(() => {
    dispatch({ type: WizardActionType.GOTO_NEXT_STEP, activeSteps });
  }, [activeSteps]);

  const goToPreviousStep = useCallback(() => {
    dispatch({ type: WizardActionType.GOTO_PREVIOUS_STEP, activeSteps });
  }, [activeSteps]);

  const value: WizardContextValue = {
    dispatch,
    currentIndex,
    currentStep,
    goToStep,
    goToErrorStep,
    goToNextStep,
    goToPreviousStep,
    onNext,
    setOnNext,
    isNextDisabled,
    setIsNextDisabled,
    isBackDisabled,
    setIsBackDisabled,
    activeSteps,
    setActiveSteps,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}
